import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap, catchError, take } from 'rxjs/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AwsClient } from 'aws4fetch';
import { AwsCredentialsService } from '../services/aws-credentials.service';
import { environment } from '@env/environment';

@Injectable()
export class XylemAuthInterceptor implements HttpInterceptor {
  constructor(
    private readonly oidcSecurityService: OidcSecurityService,
    private readonly awsCredentialsService: AwsCredentialsService
  ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    // Skip auth for OIDC token endpoints and XGS auth paths (but not XGS UM API)
    if (request.url.includes('/openid-connect/token') ||
        (request.url.includes('/xgs/') && !request.url.includes('/xgs/v1/'))) {
      return next.handle(request);
    }

    if (!this.isApiRequest(request.url)) {
      return next.handle(request);
    }

    // Use SigV4 for AWS API Gateway requests
    if (this.isAwsApiRequest(request.url)) {
      return from(this.signAwsRequest(request)).pipe(
        switchMap(signedRequest => next.handle(signedRequest)),
        catchError((error: HttpErrorResponse) => {
          if (error.status === 403) {
            // Clear cached credentials on 403 so next request gets fresh ones
            this.awsCredentialsService.clear();
            console.warn('AWS IAM auth failed (403) — credentials cleared for retry');
          }
          return throwError(() => error);
        })
      );
    }

    // Use Bearer token for non-AWS API requests
    return this.oidcSecurityService.getAccessToken().pipe(
      take(1),
      switchMap(token => {
        if (token) {
          const authRequest = request.clone({
            setHeaders: {
              // eslint-disable-next-line @typescript-eslint/naming-convention
              Authorization: `Bearer ${token}`
            }
          });
          return next.handle(authRequest);
        }
        return next.handle(request);
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === 401) {
          console.warn('Authentication failed (401) — OIDC will handle token refresh');
        }
        return throwError(() => error);
      })
    );
  }

  private async signAwsRequest(request: HttpRequest<unknown>): Promise<HttpRequest<unknown>> {
    const credentials = await this.awsCredentialsService.getCredentials();

    const client = new AwsClient({
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
      region: environment.cognito.region,
      service: 'execute-api'
    });

    const url = request.urlWithParams;
    const headers: Record<string, string> = {};
    request.headers.keys().forEach(key => {
      headers[key] = request.headers.get(key)!;
    });

    const fetchRequest = new Request(url, {
      method: request.method,
      headers,
      body: request.body ? JSON.stringify(request.body) : undefined
    });

    const signed = await client.sign(fetchRequest);

    // Extract signed headers back into Angular HttpRequest
    const signedHeaders: Record<string, string> = {};
    signed.headers.forEach((value, key) => {
      signedHeaders[key] = value;
    });

    return request.clone({ setHeaders: signedHeaders });
  }

  private isAwsApiRequest(url: string): boolean {
    return url.includes('execute-api.amazonaws.com') ||
           url.startsWith(environment.aws.baseUrl);
  }

  private isApiRequest(url: string): boolean {
    return this.isAwsApiRequest(url) ||
           url.startsWith(environment.xylem.apiBaseUrl) ||
           url.includes('cloud.xylem.com/avensor/api') ||
           url.includes('secureconnect.xylem-vue.com') ||
           url.includes('/xgs/v1/');
  }
}
